import { useState, useMemo, useEffect } from "react";
import {
  areObjectsEqual,
  focusElement,
  orderStatusList,
  inputOnEnter,
  lensesOrderStructure,
  userData,
  selectedOptic,
} from "../js/utils";
import InputIconSearch from "./InputIconSearch";
import InputData from "./InputData";
import InputDataControl from "../support-extra/components/InputDataControl";
import Dropdown from "./Dropdown";
import { OrderActionButtons } from "./OrderActionButtons";
import CheckBox from "./CheckBox";
import { post } from "../js/post";
import { useModelsTradeNamesAndProducts } from "../hooks/useModelsTradeNamesAndProducts";
import { useOrderBase } from "../hooks/useOrderBase";

export function LensesOrder({
  orderId,
  createPopupQuestion,
  removePopupQuestion,
  onSave,
  onClose,
}) {
  const {
    initialOrder,
    currentOrder,
    setCurrentOrder,
    currentOrderFixed,
    setCurrentOrderFixed,
    selectedSupplier,
    setSelectedSupplier,
    searchCode,
    setSearchCode,
    supplierList,
    brandList,
    validateOrder,
    focusFields,
    saveOrder,
    deleteOrder,
  } = useOrderBase({
    orderId,
    familia: 1,
    structure: lensesOrderStructure,
    requiredFields: ["proveedor", "marca", "nombreComercial"],
    fieldsToFocus: [
      { field: "proveedor", elementId: 51 },
      { field: "marca", elementId: 52 },
      { field: "nombreComercial", elementId: 53 },
    ],
    saveEndpoint: "support-extra/pedidos/GuardarPedidoLenteOftalmica.php",
    getOrderEndpoint: "support-extra/pedidos/ObtenerPedidoLenteOftalmica.php",
    onSave,
    createPopupQuestion,
    removePopupQuestion,
  });
  const [typeList, setTypeList] = useState([]);
  const [materialList, setMaterialList] = useState([]);
  const [rawModelsList] = useModelsTradeNamesAndProducts(
    [1],
    currentOrder.proveedor,
    currentOrder.marca !== "" ? [currentOrder.marca] : [],
  );
  const modelsList = useMemo(() => {
    return [
      { id: "", text: "" },
      ...rawModelsList.map((e) => ({
        id: e,
        text: e,
      })),
    ];
  }, [rawModelsList]);

  // console.log("currentOrder: ", currentOrder);
  // console.log("selectedSupplier: ", selectedSupplier);
  // console.log("supplierList: ", supplierList);

  const handleProductChange = (field, value) => {
    setCurrentOrder((prevState) => ({
      ...prevState,
      [field]: value,
    }));
  };

  async function getProduct() {
    const response = await post("ObtenerLenteOftalmica.php", {
      token: userData.token,
      codigo: searchCode,
      tipoCodigo: -1,
      idOptica: selectedOptic.id,
    });
    // console.log(
    // "BUSCANDO LENTE: ",
    // {
    // token: userData.token,
    // codigo: searchCode,
    // tipoCodigo: -1,
    // idOptica: selectedOptic.id,
    // },
    // response,
    // );
    if (response.id) {
      const productFields = {
        codigo: response.codigoOPTIWIN || response.codigoEANUPC,
        proveedor: response.idProveedor,
        marca: response.marca,
        nombreComercial: response.nombreComercial,
        tipo: response.tipo,
        color: response.color,
        material: response.material,
        tratamiento: response.tratamiento,
        refProveedor: response.referenciaProveedor,
        ind: response.ind,
        esf: response.esf,
        cil: response.cil,
        eje: response.eje,
        add: response.add,
        total: response.total,
        dia: response.dia,
        den: response.den,
        abbe: response.abbe,
      };
      setCurrentOrder((prev) => ({ ...prev, ...productFields }));
      setCurrentOrderFixed((prev) => ({ ...prev, ...productFields }));
      setSearchCode(productFields.codigo);
      const supplier = supplierList.find(
        (s) => s.id === parseInt(response.idProveedor),
      );
      if (supplier) setSelectedSupplier(supplier);
    } else {
      setCurrentOrder(initialOrder);
      setCurrentOrderFixed(initialOrder);
      setSelectedSupplier({ id: "", text: "" });
      createPopupQuestion(
        "Producto no encontrado.",
        async () => {
          focusElement(50);
          removePopupQuestion();
        },
        null,
        "Entendido",
      );
    }
  }

  async function getTypes() {
    const response = await post("ListarTiposLenteOftalmica.php", {
      token: userData.token,
    });
    setTypeList([
      { id: "", text: "" },
      ...response.map((e) => ({ id: e.descripcion, text: e.descripcion })),
    ]);
  }

  async function getMaterials() {
    const response = await post("ListarMaterialesLenteOftalmica.php", {
      token: userData.token,
    });
    setMaterialList([
      { id: "", text: "" },
      ...response.map((e) => ({ id: e.descripcion, text: e.descripcion })),
    ]);
  }

  useEffect(() => {
    getTypes();
    getMaterials();
  }, []);

  return (
    <div className="popup-container">
      <div className="ap-container popup-screen">
        <div className="popup-screen-header">
          <span className="popup-screen-header-title">
            Pedido de lentes oftálmicas
          </span>
          <button
            className="popup-action-icon close-icon"
            onClick={() => {
              if (!areObjectsEqual(currentOrder, currentOrderFixed)) {
                createPopupQuestion(
                  "Existen cambios sin guardar. ¿Desea salir de la ficha de pedido?",
                  async () => {
                    removePopupQuestion();
                    onClose();
                  },
                  () => {
                    removePopupQuestion();
                  },
                  "Sí",
                  "No",
                );
              } else onClose();
            }}
          >
            <img src="/images/close-white.svg" loading="lazy" alt="Icon"></img>
          </button>
        </div>
        <div
          className="tab-content ap-content"
          style={{ overflow: "unset", backgroundColor: "#f9f9f9" }}
        >
          <div
            className="container-panel"
            style={{ display: "flex", flexDirection: "column", gap: 10 }}
          >
            <div className="opt-table-title">Datos del producto</div>
            <div className="opt-table-content grid-5" id="order-product-grid">
              <div className="cell cell-label cell-start-content">
                Código/EAN/UPC
              </div>
              <div className="cell">
                <InputIconSearch
                  tabIndex={50}
                  type="type-alphanum-ext"
                  size={0}
                  maxLength={10}
                  input={searchCode}
                  onChange={(e) => {
                    setSearchCode(e);
                  }}
                  onClear={() => {
                    focusElement(50);
                    setSearchCode("");
                  }}
                  onEnter={async () => {
                    if (searchCode) await getProduct();
                  }}
                ></InputIconSearch>
              </div>
              <div className="cell combined-cells-3"></div>
              <div className="cell cell-label cell-start-content">
                Proveedor (*)
              </div>
              <div className="cell combined-cells-4">
                <Dropdown
                  tabIndex={51}
                  optionList={supplierList}
                  size={0}
                  mandatory={true}
                  op={selectedSupplier.id}
                  onChange={(e) => {
                    // console.log("PROVEEDOR SELECCIONADO: ", e);
                    setSelectedSupplier(e);
                  }}
                ></Dropdown>
              </div>
              <div className="cell cell-label cell-start-content">
                Marca (*)
              </div>
              <div className="cell combined-cells-4">
                <Dropdown
                  tabIndex={52}
                  optionList={brandList}
                  size={0}
                  mandatory={false}
                  op={currentOrder.marca}
                  onChange={(e) => {
                    setCurrentOrder((prev) => ({
                      ...prev,
                      marca: e.text,
                    }));
                  }}
                ></Dropdown>
              </div>
              <div className="cell cell-label cell-start-content">
                Nombre comercial (*)
              </div>
              <div className="cell combined-cells-4">
                <Dropdown
                  tabIndex={53}
                  optionList={modelsList}
                  size={0}
                  mandatory={false}
                  op={currentOrder.nombreComercial}
                  onChange={(e) => {
                    setCurrentOrder((prev) => ({
                      ...prev,
                      nombreComercial: e.text,
                    }));
                  }}
                ></Dropdown>
              </div>
              <div className="cell cell-label cell-start-content">Tipo</div>
              <div className="cell">
                <Dropdown
                  tabIndex={54}
                  optionList={typeList}
                  size={0}
                  mandatory={false}
                  op={currentOrder.tipo}
                  onChange={(e) => {
                    setCurrentOrder((prev) => ({
                      ...prev,
                      tipo: e.text,
                    }));
                  }}
                ></Dropdown>
              </div>
              <div className="cell"></div>
              <div className="cell cell-label cell-start-content">Color</div>
              <div className="cell">
                <InputData
                  tabIndex={57}
                  type="type-alphanum-ext"
                  size={0}
                  input={currentOrder.color}
                  onChange={(e) => {
                    setCurrentOrder((prev) => ({
                      ...prev,
                      color: e,
                    }));
                  }}
                ></InputData>
              </div>
              <div className="cell cell-label cell-start-content">Material</div>
              <div className="cell">
                <Dropdown
                  tabIndex={55}
                  optionList={materialList}
                  size={0}
                  op={currentOrder.material}
                  onChange={(e) => {
                    setCurrentOrder((prev) => ({
                      ...prev,
                      material: e.text,
                    }));
                  }}
                ></Dropdown>
              </div>
              <div className="cell"></div>
              <div className="cell cell-label cell-start-content">Ojo</div>
              <div className="cell">
                <Dropdown
                  tabIndex={58}
                  optionList={[
                    { id: "DER", text: "DERECHO" },
                    { id: "IZQ", text: "IZQUIERDO" },
                  ]}
                  size={0}
                  op={currentOrder.ojo}
                  onChange={(e) => {
                    setCurrentOrder((prev) => ({
                      ...prev,
                      ojo: e.id,
                    }));
                  }}
                ></Dropdown>
              </div>
              <div className="cell cell-label cell-start-content">
                Tratamiento
              </div>
              <div className="cell">
                <InputData
                  tabIndex={56}
                  maxLength={40}
                  type="type-alphanum-ext"
                  size={0}
                  input={currentOrder.tratamiento}
                  onChange={(e) => {
                    setCurrentOrder((prev) => ({
                      ...prev,
                      tratamiento: e,
                    }));
                  }}
                ></InputData>
              </div>
              <div className="cell"></div>
              <div className="cell cell-label cell-start-content">
                Ref. proveedor
              </div>
              <div className="cell">
                <InputData
                  tabIndex={60}
                  maxLength={20}
                  type="type-alphanum-ext"
                  size={0}
                  input={currentOrder.refProveedor}
                  onChange={(e) => {
                    setCurrentOrder((prev) => ({
                      ...prev,
                      refProveedor: e,
                    }));
                  }}
                ></InputData>
              </div>
              <div className="cell combined-cells-5">
                <div className="opt-table-content grid-9">
                  <div className="cell cell-label">Índ</div>
                  <div className="cell cell-label">Esf</div>
                  <div className="cell cell-label">Cil</div>
                  <div className="cell cell-label">Eje</div>
                  <div className="cell cell-label">Add</div>
                  <div className="cell cell-label">Total</div>
                  <div className="cell cell-label">Diá</div>
                  <div className="cell cell-label">Den</div>
                  <div className="cell cell-label">Abbe</div>
                  <div className="cell">
                    <InputData
                      tabIndex={61}
                      type="type-float2"
                      size={0}
                      input={currentOrder.ind}
                      onChange={(e) => {
                        handleProductChange("ind", e);
                      }}
                    ></InputData>
                  </div>
                  <div className="cell">
                    <InputDataControl
                      tabIndex={62}
                      type="type-optofloat"
                      size={0}
                      input={currentOrder.esf}
                      minValue=""
                      maxValue=""
                      step={0.25}
                      numDecimales=""
                      createPopupQuestion={createPopupQuestion}
                      removePopupQuestion={removePopupQuestion}
                      onChange={(e) => {
                        handleProductChange("esf", e);
                      }}
                    ></InputDataControl>
                  </div>
                  <div className="cell">
                    <InputDataControl
                      tabIndex={63}
                      type="type-optofloat"
                      size={0}
                      input={currentOrder.cil}
                      minValue=""
                      maxValue=""
                      step={0.25}
                      numDecimales=""
                      createPopupQuestion={createPopupQuestion}
                      removePopupQuestion={removePopupQuestion}
                      onChange={(e) => {
                        handleProductChange("cil", e);
                      }}
                    ></InputDataControl>
                  </div>
                  <div className="cell">
                    <InputData
                      tabIndex={64}
                      type="type-integer"
                      size={0}
                      input={currentOrder.eje}
                      onChange={(e) => {
                        handleProductChange("eje", e);
                      }}
                    ></InputData>
                  </div>
                  <div className="cell">
                    <InputDataControl
                      tabIndex={65}
                      type="type-optofloat"
                      size={0}
                      input={currentOrder.add}
                      minValue=""
                      maxValue=""
                      step={0.25}
                      numDecimales=""
                      createPopupQuestion={createPopupQuestion}
                      removePopupQuestion={removePopupQuestion}
                      onChange={(e) => {
                        handleProductChange("add", e);
                      }}
                    ></InputDataControl>
                  </div>
                  <div className="cell">
                    <InputData
                      tabIndex={66}
                      type="type-float2"
                      size={0}
                      input={currentOrder.total}
                      onChange={(e) => {
                        handleProductChange("total", e);
                      }}
                    ></InputData>
                  </div>
                  <div className="cell">
                    <InputData
                      tabIndex={67}
                      size={0}
                      maxLength={15}
                      forceCenter={true}
                      input={currentOrder.dia}
                      onChange={(e) => {
                        handleProductChange("dia", e);
                      }}
                    ></InputData>
                  </div>
                  <div className="cell">
                    <InputData
                      tabIndex={68}
                      type="type-float2"
                      size={0}
                      input={currentOrder.den}
                      onChange={(e) => {
                        handleProductChange("den", e);
                      }}
                    ></InputData>
                  </div>
                  <div className="cell">
                    <InputData
                      tabIndex={69}
                      type="type-float2"
                      size={0}
                      input={currentOrder.abbe}
                      onChange={(e) => {
                        handleProductChange("abbe", e);
                      }}
                    ></InputData>
                  </div>
                </div>
              </div>
            </div>
            <div className="opt-table-title" style={{ marginTop: 20 }}>
              Datos del pedido:{" "}
              {currentOrder.idPedido === -1
                ? selectedOptic.nombre
                : currentOrder.nombreOptica}
            </div>
            <div className="opt-table-content grid-5" id="order-product-grid">
              <div className="cell cell-label cell-start-content">
                Fecha pedido
              </div>
              <div className="cell">
                <input
                  type="date"
                  max="9999-12-31"
                  tabIndex={70}
                  className="input-field input-cell"
                  value={currentOrder.fechaPedido}
                  onChange={(e) => {
                    setCurrentOrder((prev) => ({
                      ...prev,
                      fechaPedido: e.target.value,
                    }));
                  }}
                  onKeyUp={(e) => inputOnEnter(e)}
                ></input>
              </div>
              <div className="cell"></div>
              <div className="cell cell-label cell-start-content">Estado</div>
              <div className="cell">
                <Dropdown
                  tabIndex={74}
                  optionList={orderStatusList}
                  size={0}
                  op={currentOrder.estado}
                  onChange={(e) => {
                    setCurrentOrder((prev) => ({
                      ...prev,
                      estado: e.id,
                    }));
                  }}
                ></Dropdown>
              </div>
              <div className="cell cell-label cell-start-content">
                Fecha recepción
              </div>
              <div className="cell">
                <input
                  type="date"
                  max="9999-12-31"
                  tabIndex={71}
                  className="input-field input-cell"
                  value={currentOrder.fechaRecepcion}
                  onChange={(e) => {
                    setCurrentOrder((prev) => ({
                      ...prev,
                      fechaRecepcion: e.target.value,
                    }));
                  }}
                  onKeyUp={(e) => inputOnEnter(e)}
                ></input>
              </div>
              <div className="cell"></div>
              <div className="cell cell-label cell-start-content">Unidades</div>
              <div className="cell">
                <button
                  className="incdec-btn"
                  onClick={() => {
                    setCurrentOrder((prev) => ({
                      ...prev,
                      unidades:
                        parseInt(prev.unidades) >= 2
                          ? parseInt(prev.unidades) - 1
                          : parseInt(prev.unidades),
                    }));
                  }}
                >
                  -
                </button>
                <div style={{ flex: 1 }}>
                  <InputData
                    tabIndex={75}
                    type="type-integer"
                    size={0}
                    input={currentOrder.unidades}
                    onChange={(e) => {
                      setCurrentOrder((prev) => ({
                        ...prev,
                        unidades: e,
                      }));
                    }}
                  ></InputData>
                </div>
                <button
                  className="incdec-btn"
                  onClick={() => {
                    setCurrentOrder((prev) => ({
                      ...prev,
                      unidades: parseInt(prev.unidades) + 1,
                    }));
                  }}
                >
                  +
                </button>
              </div>
              <div className="cell cell-label cell-start-content">
                Pedido automatizado
              </div>
              <div className="cell cell-start-content">
                <CheckBox
                  tabIndex={72}
                  active={currentOrder.pedidoAutomatizado}
                  onClick={() => {
                    setCurrentOrder((prev) => ({
                      ...prev,
                      pedidoAutomatizado: !prev.pedidoAutomatizado,
                    }));
                  }}
                ></CheckBox>
              </div>
              <div className="cell"></div>
              <div className="cell cell-label cell-start-content">
                ID Pedido
              </div>
              <div className="cell">
                <InputData
                  tabIndex={76}
                  maxLength={20}
                  type="type-alphanum-ext"
                  size={0}
                  input={currentOrder.idPedidoAutomatizado}
                  onChange={(e) => {
                    setCurrentOrder((prev) => ({
                      ...prev,
                      idPedidoAutomatizado: e,
                    }));
                  }}
                ></InputData>
              </div>
              <div className="cell cell-label cell-start-content">
                URL Pedido
              </div>
              <div className="cell combined-cells-4">
                <InputData
                  tabIndex={73}
                  maxLength={500}
                  size={0}
                  input={currentOrder.urlPedidoAutomatizado}
                  onChange={(e) => {
                    setCurrentOrder((prev) => ({
                      ...prev,
                      urlPedidoAutomatizado: e,
                    }));
                  }}
                  icon="/images/link.svg"
                  onClick={() => {
                    const url = currentOrder.urlPedidoAutomatizado;
                    if (url) {
                      const full = /^https?:\/\//i.test(url)
                        ? url
                        : `https://${url}`;
                      window.open(full, "_blank");
                    }
                  }}
                ></InputData>
              </div>
            </div>
            <OrderActionButtons
              validateOrder={validateOrder}
              focusFields={focusFields}
              saveOrder={saveOrder}
              deleteOrder={deleteOrder}
              onSave={onSave}
              onClose={onClose}
              idPedido={currentOrder.idPedido}
              createPopupQuestion={createPopupQuestion}
              removePopupQuestion={removePopupQuestion}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
